/**
 * Tiptap rich-text editor matching the previous Froala toolbar layout.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import Image from "@tiptap/extension-image";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { useAsterController } from "context";
import { sanitizeFroalaHtml } from "utils/string";
import { asBool, asFunc, asNumber, asString } from "utils/safeData";
import "./VFroala.css";

const COLORS = ["#000000", "#e60000", "#ff9900", "#ffff00", "#008a00", "#0066cc", "#9933ff", "#ffffff", "#facccc", "#ffebcc", "#ffffcc", "#cce8cc", "#cce0f5", "#ebd6ff"];

/**
 * Rich-text input used in chat and comments. Sanitizes HTML on Enter submit.
 *
 * @param {{ onSubmit?: Function, fileUpload?: boolean, className?: string, maxCount?: number, isEnterSubmit?: boolean, isChat?: boolean, disabled?: boolean, model?: string, setModel?: Function }} props
 */
const VFroala = (props) => {
  const { t } = useTranslation("common");
  const onSubmit = asFunc(props.onSubmit);
  const fileUpload = asBool(props.fileUpload);
  const className = asString(props.className);
  const maxCount = props.maxCount == null ? null : asNumber(props.maxCount);
  const isEnterSubmit = asBool(props.isEnterSubmit);
  const isChat = asBool(props.isChat);
  const disabled = asBool(props.disabled);
  const model = props.model == null ? "" : asString(props.model);
  const setModel = asFunc(props.setModel);

  const [controller] = useAsterController();
  const { darkMode } = controller;
  const [fullscreen, setFullscreen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [headingOpen, setHeadingOpen] = useState(false);

  const editorRef = useRef(null);
  const headingWrapRef = useRef(null);
  const colorWrapRef = useRef(null);
  const latestRef = useRef({ disabled, isEnterSubmit, onSubmit, setModel });
  useEffect(() => {
    latestRef.current = { disabled, isEnterSubmit, onSubmit, setModel };
  }, [disabled, isEnterSubmit, onSubmit, setModel]);

  useEffect(() => {
    if (!headingOpen && !colorOpen) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (headingWrapRef.current?.contains(target) || colorWrapRef.current?.contains(target)) return;
      setHeadingOpen(false);
      setColorOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setHeadingOpen(false);
        setColorOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [headingOpen, colorOpen]);

  const extensions = useMemo(() => {
    const list = [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, horizontalRule: false }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: t("Type...") }),
      CharacterCount.configure(maxCount && !isChat ? { limit: maxCount } : {}),
    ];
    if (fileUpload && !isChat) list.push(Image.configure({ inline: false, allowBase64: true }));
    return list;
  }, [fileUpload, isChat, maxCount, t]);

  const editor = useEditor({
    extensions,
    content: model || "",
    editable: !disabled,
    onCreate: ({ editor: next }) => {
      editorRef.current = next;
    },
    onUpdate: ({ editor: next }) => {
      latestRef.current.setModel(next.getHTML());
    },
    editorProps: {
      handleKeyDown: (_view, evt) => {
        const cur = latestRef.current;
        const currentEditor = editorRef.current;
        if (cur.disabled || !currentEditor) return false;
        const isEnter = (evt.key === "Enter" || evt.keyCode === 13) && !evt.altKey && !evt.shiftKey && !evt.ctrlKey;
        if (cur.isEnterSubmit && isEnter) {
          evt.preventDefault();
          const html = sanitizeFroalaHtml(currentEditor.getHTML());
          if (html) cur.onSubmit(html);
          currentEditor.commands.clearContent(false);
          cur.setModel("");
          return true;
        }
        return false;
      },
    },
  }, [extensions]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;
    const next = model || "";
    if (next !== editor.getHTML()) editor.commands.setContent(next, false);
  }, [model, editor]);

  const run = useCallback((fn) => {
    if (!editor || disabled) return;
    fn();
  }, [disabled, editor]);

  const insertImage = () => {
    if (!editor || disabled) return;
    const url = window.prompt(t("Type..."));
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  if (!editor) return <div className={`v-tiptap ${className}`} />;

  return (
    <div className={`v-tiptap ${fullscreen ? "v-tiptap--fullscreen" : ""} ${darkMode ? "dark-theme" : ""} ${className}`}>
      <div className="v-tiptap__toolbar">
        <div className="v-tiptap__group">
          <button type="button" className={`v-tiptap__btn ${editor.isActive("bold") ? "is-active" : ""}`} disabled={disabled} onClick={() => run(() => editor.chain().focus().toggleBold().run())}><b>B</b></button>
          <button type="button" className={`v-tiptap__btn ${editor.isActive("italic") ? "is-active" : ""}`} disabled={disabled} onClick={() => run(() => editor.chain().focus().toggleItalic().run())}><i>I</i></button>
          <button type="button" className={`v-tiptap__btn ${editor.isActive("underline") ? "is-active" : ""}`} disabled={disabled} onClick={() => run(() => editor.chain().focus().toggleUnderline().run())}><u>U</u></button>
        </div>
        <div className="v-tiptap__group">
          <div className="v-tiptap__color-wrap" ref={colorWrapRef}>
            <button type="button" className="v-tiptap__btn" disabled={disabled} onClick={() => { setHeadingOpen(false); setColorOpen((v) => !v); }} title="Color">
              <span style={{ borderBottom: "3px solid #d94133", lineHeight: 1, fontWeight: 700 }}>A</span>
            </button>
            {colorOpen && (
              <div className="v-tiptap__swatches">
                {COLORS.map((c) => (
                  <button key={c} type="button" className="v-tiptap__swatch" style={{ background: c }} onClick={() => { run(() => editor.chain().focus().setColor(c).run()); setColorOpen(false); }} />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="v-tiptap__group">
          <button type="button" className={`v-tiptap__btn ${editor.isActive({ textAlign: "left" }) ? "is-active" : ""}`} disabled={disabled} onClick={() => run(() => editor.chain().focus().setTextAlign("left").run())}>
            <Icon icon="ic:round-format-align-left" className="text-lg" />
          </button>
          <button type="button" className={`v-tiptap__btn ${editor.isActive({ textAlign: "center" }) ? "is-active" : ""}`} disabled={disabled} onClick={() => run(() => editor.chain().focus().setTextAlign("center").run())}>
            <Icon icon="ic:round-format-align-center" className="text-lg" />
          </button>
        </div>
        <div className="v-tiptap__group">
          <div className="v-tiptap__color-wrap" ref={headingWrapRef}>
            <button
              type="button"
              className={`v-tiptap__btn ${headingOpen ? "is-active" : ""}`}
              disabled={disabled}
              onClick={() => { setColorOpen(false); setHeadingOpen((v) => !v); }}
            >
              {editor.isActive("heading", { level: 1 }) ? "H1" : editor.isActive("heading", { level: 2 }) ? "H2" : editor.isActive("heading", { level: 3 }) ? "H3" : "¶"}
              <Icon icon="ic:round-keyboard-arrow-down" className="text-base ml-0.5" />
            </button>
            {headingOpen && (
              <div className="v-tiptap__menu">
                {[
                  { value: "p", label: "¶" },
                  { value: "h1", label: "H1" },
                  { value: "h2", label: "H2" },
                  { value: "h3", label: "H3" },
                ].map((item) => {
                  const active =
                    item.value === "p"
                      ? !editor.isActive("heading")
                      : editor.isActive("heading", { level: Number(item.value.slice(1)) });
                  return (
                    <button
                      key={item.value}
                      type="button"
                      className={`v-tiptap__menu-item ${active ? "is-active" : ""}`}
                      onClick={() => {
                        run(() => {
                          if (item.value === "p") editor.chain().focus().setParagraph().run();
                          else editor.chain().focus().toggleHeading({ level: Number(item.value.slice(1)) }).run();
                        });
                        setHeadingOpen(false);
                      }}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {fileUpload && !isChat && (
            <button type="button" className="v-tiptap__btn" disabled={disabled} onClick={insertImage}>
              <Icon icon="ic:round-image" className="text-lg" />
            </button>
          )}
        </div>
        <div className="v-tiptap__group" style={{ marginLeft: "auto" }}>
          <button type="button" className="v-tiptap__btn" disabled={disabled || !editor.can().undo()} onClick={() => run(() => editor.chain().focus().undo().run())}>
            <Icon icon="ic:round-undo" className="text-lg" />
          </button>
          <button type="button" className="v-tiptap__btn" disabled={disabled || !editor.can().redo()} onClick={() => run(() => editor.chain().focus().redo().run())}>
            <Icon icon="ic:round-redo" className="text-lg" />
          </button>
          <button type="button" className={`v-tiptap__btn ${fullscreen ? "is-active" : ""}`} onClick={() => setFullscreen((v) => !v)}>
            <Icon icon={fullscreen ? "ic:round-fullscreen-exit" : "ic:round-fullscreen"} className="text-lg" />
          </button>
        </div>
      </div>
      <div className="v-tiptap__content">
        <EditorContent editor={editor} />
      </div>
      {!isChat && maxCount ? (
        <div className="v-tiptap__counter">{editor.storage.characterCount.characters()} / {maxCount}</div>
      ) : null}
    </div>
  );
};

VFroala.defaultProps = {
  fileUpload: false,
  className: "",
  maxCount: null,
  disabled: false,
};

VFroala.propTypes = {
  onSubmit: PropTypes.func,
  fileUpload: PropTypes.bool,
  className: PropTypes.string,
  maxCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isEnterSubmit: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
  isChat: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
  model: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setModel: PropTypes.func,
};

export default VFroala;
